import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Lock, Archive } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/shared/Badge';
import Modal from '../../components/shared/Modal';

// Funil de Leads — etapas configuráveis (funil_leads_estagios, migration 094): criar, renomear, mudar cor,
// mover e excluir (gerente/admin). Etapas com cadeado são gravadas pelo próprio sistema (Cal.com, portal,
// pipeline...) — dá para renomear e mover, mas não excluir. Cards: arquivar (→ Perdido) ou excluir de vez.

const CORES = ['bg-gray-400', 'bg-blue-500', 'bg-red-500', 'bg-indigo-500', 'bg-purple-500',
  'bg-yellow-500', 'bg-green-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500', 'bg-red-400', 'bg-cyan-500'];
const erroMsg = (err) => err?.response?.data?.message || err?.response?.data?.error || err?.message;
const usuario = () => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } };

function EtapaModal({ isOpen, onClose, estagio, onSalvo }) {
  const [label, setLabel] = useState('');
  const [cor, setCor] = useState(CORES[1]);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (isOpen) { setLabel(estagio?.label || ''); setCor(estagio?.cor || CORES[1]); setErro(''); } }, [isOpen, estagio]);

  const salvar = async () => {
    setErro(''); setSalvando(true);
    try {
      if (estagio) await api.put(`/leads/funil-estagios/${estagio.chave}`, { label, cor });
      else await api.post('/leads/funil-estagios', { label, cor });
      onSalvo();
    } catch (err) { setErro(erroMsg(err)); } finally { setSalvando(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={estagio ? `Editar etapa "${estagio.label}"` : 'Nova etapa'} size="sm">
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-gray-600">Nome</span>
          <input className="input-field w-full mt-1" autoFocus maxLength={100} value={label} onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && label.trim()) salvar(); }} />
        </label>
        <div className="text-sm">
          <span className="text-gray-600">Cor</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {CORES.map((c) => (
              <button key={c} type="button" onClick={() => setCor(c)} title={c}
                className={`${c} w-7 h-7 rounded-full ${cor === c ? 'ring-2 ring-offset-2 ring-gray-700' : ''}`} />
            ))}
          </div>
        </div>
        {!estagio && <p className="text-xs text-gray-500">A etapa nova entra antes de "Vendido" e "Perdido". Depois dá para mudar a posição pelas setas.</p>}
        {erro && <p className="text-sm text-red-600">{erro}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="button" onClick={salvar} disabled={salvando || !label.trim()} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ExcluirEtapaModal({ isOpen, onClose, estagio, estagios, total: totalLocal, onExcluida }) {
  const [destino, setDestino] = useState('');
  const [erro, setErro] = useState('');
  const [excluindo, setExcluindo] = useState(false);
  const [totalServidor, setTotalServidor] = useState(null);   // o 409 do backend corrige a contagem local
  useEffect(() => { if (isOpen) { setDestino(''); setErro(''); setTotalServidor(null); } }, [isOpen, estagio]);
  if (!estagio) return null;
  const total = totalServidor ?? totalLocal;
  const outras = estagios.filter((e) => e.chave !== estagio.chave);

  const confirmar = async () => {
    setErro(''); setExcluindo(true);
    try {
      await api.delete(`/leads/funil-estagios/${estagio.chave}`, { data: destino ? { destino_chave: destino } : {} });
      onExcluida();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.total) setTotalServidor(err.response.data.total);
      else setErro(erroMsg(err));
    } finally { setExcluindo(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Excluir etapa "${estagio.label}"`} size="sm">
      <div className="space-y-3">
        {total > 0 ? (
          <>
            <p className="text-sm text-gray-600">Essa etapa tem <strong>{total}</strong> lead(s). Para qual etapa eles vão?</p>
            <select value={destino} onChange={(e) => setDestino(e.target.value)} className="input-field w-full">
              <option value="">Selecione...</option>
              {outras.map((e) => <option key={e.chave} value={e.chave}>{e.label}</option>)}
            </select>
          </>
        ) : <p className="text-sm text-gray-600">A etapa está vazia. Excluir?</p>}
        {erro && <p className="text-sm text-red-600">{erro}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="button" onClick={confirmar} disabled={excluindo || (total > 0 && !destino)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
            {excluindo ? 'Excluindo...' : 'Excluir etapa'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ExcluirLeadModal({ isOpen, onClose, lead, podeExcluir, onFeito }) {
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(null);
  useEffect(() => { if (isOpen) { setErro(''); setOcupado(null); } }, [isOpen, lead]);
  if (!lead) return null;

  const executar = async (tipo) => {
    setErro(''); setOcupado(tipo);
    try {
      if (tipo === 'arquivar') await api.put(`/leads/${lead.id}`, { estagio_funil: 'perdido' });
      else await api.delete(`/leads/${lead.id}/definitivo`);
      onFeito();
    } catch (err) { setErro(erroMsg(err)); } finally { setOcupado(null); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Tirar "${lead.nome}" do funil`} size="sm">
      <div className="space-y-3 text-sm">
        <button type="button" onClick={() => executar('arquivar')} disabled={!!ocupado || lead.estagio_funil === 'perdido'}
          className="w-full text-left border border-gray-200 rounded-lg p-3 hover:border-gray-400 disabled:opacity-50">
          <div className="font-medium text-gray-800 flex items-center gap-2"><Archive size={15} /> {ocupado === 'arquivar' ? 'Movendo...' : 'Mover para Perdido'}</div>
          <div className="text-xs text-gray-500 mt-1">O lead e o histórico continuam no CRM, na coluna Perdido.</div>
        </button>
        {podeExcluir ? (
          <button type="button" onClick={() => executar('excluir')} disabled={!!ocupado}
            className="w-full text-left border border-red-200 rounded-lg p-3 hover:border-red-400 disabled:opacity-50">
            <div className="font-medium text-red-700 flex items-center gap-2"><Trash2 size={15} /> {ocupado === 'excluir' ? 'Excluindo...' : 'Excluir definitivamente'}</div>
            <div className="text-xs text-gray-500 mt-1">Apaga o lead e os atendimentos dele. Não dá para desfazer. Leads com proposta, pré-reserva ou negócio não podem ser excluídos.</div>
          </button>
        ) : <p className="text-xs text-gray-500">Excluir definitivamente é só para gerente ou admin.</p>}
        {erro && <p className="text-sm text-red-600">{erro}</p>}
      </div>
    </Modal>
  );
}

export default function LeadKanban() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const perfil = usuario()?.perfil;
  const gerente = ['admin', 'gerente'].includes(perfil);
  const [editando, setEditando] = useState(undefined);        // undefined = fechado, null = nova, objeto = editar
  const [excluindoEtapa, setExcluindoEtapa] = useState(null);
  const [tirando, setTirando] = useState(null);
  const [verPerdidos, setVerPerdidos] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['leads-kanban'],
    queryFn: () => api.get('/leads/pipeline').then((r) => ({ grupos: r.data.data || {}, estagios: r.data.estagios || [] })),
    refetchInterval: 30000,
  });
  const atualizar = () => qc.invalidateQueries({ queryKey: ['leads-kanban'] });

  const mover = useMutation({
    mutationFn: ({ id, estagio_funil }) => api.put(`/leads/${id}`, { estagio_funil }),
    onSuccess: atualizar,
    onError: (err) => { alert(erroMsg(err)); atualizar(); },
  });
  const reordenar = useMutation({
    mutationFn: (ordem) => api.put('/leads/funil-estagios/reordenar', { ordem }),
    onSuccess: atualizar,
    onError: (err) => alert(erroMsg(err)),
  });

  if (isLoading) return <div className="p-8 text-center text-gray-400">Carregando...</div>;
  const kanban = data?.grupos || {};
  const todos = data?.estagios || [];
  const estagios = verPerdidos ? todos : todos.filter((e) => e.chave !== 'perdido');

  function onDragEnd(result) {
    if (!result.destination || result.destination.droppableId === result.source.droppableId) return;
    mover.mutate({ id: result.draggableId, estagio_funil: result.destination.droppableId });
  }

  function moverEtapa(chave, delta) {
    const ordem = todos.map((e) => e.chave);
    const i = ordem.indexOf(chave);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ordem.length) return;
    [ordem[i], ordem[j]] = [ordem[j], ordem[i]];
    reordenar.mutate(ordem);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Funil de Leads</h1>
        <label className="flex items-center gap-2 text-sm text-gray-600 ml-auto">
          <input type="checkbox" checked={verPerdidos} onChange={(e) => setVerPerdidos(e.target.checked)} /> Mostrar Perdidos ({(kanban.perdido || []).length})
        </label>
        {gerente && (
          <button onClick={() => setEditando(null)} className="flex items-center gap-1 bg-primary text-white px-3 py-2 rounded-lg text-sm font-medium">
            <Plus size={15} /> Nova etapa
          </button>
        )}
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {estagios.map((col) => {
            const pos = todos.findIndex((e) => e.chave === col.chave);
            return (
              <div key={col.chave} className="flex-shrink-0 w-64">
                <div className={`${col.cor} text-white px-3 py-2 rounded-t-lg flex items-center gap-1 group`}>
                  {col.protegida && <span title="Etapa usada pelo sistema — dá para renomear e mover, mas não excluir"><Lock size={11} className="opacity-70 shrink-0" /></span>}
                  <span className="text-sm font-semibold truncate">{col.label}</span>
                  <span className="bg-white bg-opacity-30 text-xs px-2 py-0.5 rounded-full ml-1">{kanban[col.chave]?.length || 0}</span>
                  {gerente && (
                    <span className="ml-auto flex items-center gap-0.5 opacity-80">
                      <button onClick={() => moverEtapa(col.chave, -1)} disabled={pos <= 0 || reordenar.isPending} title="Mover para a esquerda" className="p-0.5 hover:bg-white/20 rounded disabled:opacity-30"><ChevronLeft size={14} /></button>
                      <button onClick={() => moverEtapa(col.chave, 1)} disabled={pos >= todos.length - 1 || reordenar.isPending} title="Mover para a direita" className="p-0.5 hover:bg-white/20 rounded disabled:opacity-30"><ChevronRight size={14} /></button>
                      <button onClick={() => setEditando(col)} title="Renomear / mudar cor" className="p-0.5 hover:bg-white/20 rounded"><Pencil size={13} /></button>
                      {!col.protegida && <button onClick={() => setExcluindoEtapa(col)} title="Excluir etapa" className="p-0.5 hover:bg-white/20 rounded"><Trash2 size={13} /></button>}
                    </span>
                  )}
                </div>
                <Droppable droppableId={col.chave}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}
                      className={`kanban-col bg-gray-100 rounded-b-lg p-2 space-y-2 min-h-32 ${snapshot.isDraggingOver ? 'bg-blue-50' : ''}`}>
                      {(kanban[col.chave] || []).map((lead, index) => (
                        <Draggable key={lead.id} draggableId={lead.id} index={index}>
                          {(prov, snap) => (
                            <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                              onClick={() => navigate(`/leads/${lead.id}`)}
                              className={`group/card relative bg-white rounded-lg p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${snap.isDragging ? 'rotate-1 shadow-lg' : ''}`}>
                              <button onClick={(e) => { e.stopPropagation(); setTirando(lead); }} title="Tirar do funil / excluir"
                                className="absolute top-2 right-2 p-1 rounded text-gray-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover/card:opacity-100">
                                <Trash2 size={14} />
                              </button>
                              <p className="font-semibold text-gray-800 text-sm truncate pr-6">{lead.nome}</p>
                              <p className="text-xs text-gray-500 mt-1">{lead.telefone}</p>
                              {lead.empreendimento_nome && <p className="text-xs text-secondary mt-1 truncate">{lead.empreendimento_nome}</p>}
                              <div className="flex items-center justify-between mt-2">
                                <Badge label={lead.origem?.replace(/_/g, ' ')} variant={lead.origem} />
                                <span className="text-sm">{lead.temperatura === 'quente' ? '🔴' : lead.temperatura === 'morno' ? '🟡' : '🔵'}</span>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      <EtapaModal isOpen={editando !== undefined} onClose={() => setEditando(undefined)} estagio={editando} onSalvo={() => { setEditando(undefined); atualizar(); }} />
      <ExcluirEtapaModal isOpen={!!excluindoEtapa} onClose={() => setExcluindoEtapa(null)} estagio={excluindoEtapa} estagios={todos}
        total={excluindoEtapa ? (kanban[excluindoEtapa.chave] || []).length : 0} onExcluida={() => { setExcluindoEtapa(null); atualizar(); }} />
      <ExcluirLeadModal isOpen={!!tirando} onClose={() => setTirando(null)} lead={tirando} podeExcluir={gerente} onFeito={() => { setTirando(null); atualizar(); }} />
    </div>
  );
}
